<template>
    <div v-if="!isLoading">
        <data-table
            v-if="collection.columns.length > 0"
            :collection="collection"
            :is-loading="isLoading"
            :row-set="mainHasDefaultRows ? null : rows"
            :additional-columns="additionalColumns"
            with-scroll-sync
        >
            <template #title>
                <div class="flex justify-between items-center">
                    <span>{{ $t('general.overall') }}</span>

                    <default-button
                        :color="mainHasDefaultRows ? 'green' : 'indigo'"
                        :label="$t('storemanager.show-all-rows')"
                        class="ml-2"
                        icon="color-swatch"
                        size="xxs"
                        @click.native="mainHasDefaultRows = !mainHasDefaultRows"
                    ></default-button>
                </div>
            </template>

            <template #title-actions>
                <default-button
                    :label="showProjectionColumns ? $t('storemanager.hide-projection-columns') : $t('storemanager.show-projection-columns')"
                    icon="folder"
                    size="xxs"
                    :color="showProjectionColumns ? 'green' : 'orange'"
                    @click.native="toggleShowProjectionColumns"
                ></default-button>
            </template>
        </data-table>

        <template v-for="data in storeData">
            <budget-evaluation-table
                :key="data.store.id"
                :data="data"
                :date="date"
                :is-loading="isLoading"
                :rows="rows"
            />
        </template>
    </div>
</template>

<script>
import DataTable from '@/Components/Storemanager/DataTable';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import BudgetEvaluationTable from '@/Pages/Evaluation/Components/BudgetEvaluationTable';
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import BudgetEntry from '@/Models/BudgetEntry';
import Store from '@/Models/Store';
import BudgetProjectionEntry from '@/Models/BudgetProjectionEntry';
import Actual from '@/Models/Actual';
import FieldsModel from '@/Models/Contracts/FieldsModel';

export default {
    components: {BudgetEvaluationTable, DataTable},

    props: {
        evaluationData: Object,
        date: Date,
    },

    data() {
        return {
            isLoading: true,
            mainHasDefaultRows: false,
            rows: [
                storeManager.getFieldByNumber(1),
                storeManager.getFieldByNumber(52),
                storeManager.getFieldByNumber(60),
                storeManager.getFieldByNumber(98),
                storeManager.getFieldByNumber(105),
                storeManager.getFieldByNumber(600),
            ],

            collection: new DataColumnCollection([], true),
            storeData: [],
            additionalColumns: new DataColumnCollection([], true),
            showProjectionColumns: true,
        };
    },

    created() {
        this.prepareColumns(this.evaluationData);
        this.isLoading = false;
    },

    computed: {
        currentDate() {
            return moment.utc(this.date);
        },
    },

    methods: {
        toggleShowProjectionColumns() {
            this.showProjectionColumns = !this.showProjectionColumns;
            this.prepareColumns(this.evaluationData);
            this.$emit('projection-toggle', this.showProjectionColumns);
        },

        prepareColumns(data) {
            let columns = [];
            let months = _.range(0, 12);
            let storeData = [];
            let additionalColumns = [];

            if (!data.hasOwnProperty('results')) {
                return;
            }

            let ytdColumn = new DataColumn(
                new FieldsModel(data.results?.accumulatedPreviousYtd ?? {}, false),
            );

            ytdColumn.header = this.$t('general.ytd').toString() + ' ' + (moment.utc(this.date).year() - 1);
            additionalColumns.push(ytdColumn);

            let accumulatedColumn = new DataColumn(
                new FieldsModel(data.results?.accumulated ?? {}, false),
            );

            accumulatedColumn.header = this.$t('general.budget')
                .toString() + ' ' + moment.utc(this.date).year() + ' ' + this.$t('general.ytd').toString();

            additionalColumns.push(accumulatedColumn);

            months.forEach(
                month => {
                    let date = moment.utc(this.date).set('month', month);
                    let dateLastYear = moment.utc(this.date).set('month', month).subtract(1, 'year');

                    const budgetEntry = data.results.budgetEntries.find(
                        budgetEntry => moment.utc(budgetEntry.date).isSame(date, 'month'),
                    );

                    columns.push(
                        new DataColumn(
                            new BudgetEntry(budgetEntry ?? {date: date}, false),
                        ),
                    );

                    if (!this.showProjectionColumns) {
                        return;
                    }

                    const actual = data.results.actuals.find(
                        actual => moment.utc(actual.date).isSame(dateLastYear, 'month'),
                    );

                    if (actual) {
                        columns.push(
                            new DataColumn(
                                new Actual(actual, false),
                            ),
                        );

                        return;
                    }

                    const budgetProjectionEntry = data.results.budgetProjectionEntries.find(
                        budgetProjectionEntry => moment.utc(budgetProjectionEntry.date).isSame(dateLastYear, 'month'),
                    );

                    if (budgetProjectionEntry) {
                        columns.push(
                            new DataColumn(
                                new BudgetProjectionEntry(budgetProjectionEntry, false),
                            ),
                        );
                    }
                },
            );

            if (data.hasOwnProperty('storeData') && Array.isArray(data.storeData) && data.storeData.length > 0) {
                data.storeData.forEach(storeDataEntry => {
                    let store = new Store(storeDataEntry.store);
                    let storeColumns = [];

                    columns.forEach(
                        column => {
                            const entity = column.entity;

                            if (entity instanceof BudgetEntry) {
                                const budgetEntry = storeDataEntry.budgetEntries?.find(
                                    budgetEntry => moment.utc(budgetEntry.date).isSame(entity.date, 'month'),
                                );

                                storeColumns.push(
                                    new DataColumn(
                                        new BudgetEntry(budgetEntry ?? {date: entity.date}, false),
                                    ),
                                );
                            }

                            if (!this.showProjectionColumns) {
                                return;
                            }

                            if (entity instanceof Actual) {
                                const actual = storeDataEntry.actuals?.find(
                                    actual => moment.utc(actual.date).isSame(entity.date, 'month'),
                                );

                                storeColumns.push(
                                    new DataColumn(
                                        new Actual(actual ?? {date: entity.date}, false),
                                    ),
                                );
                            }

                            if (entity instanceof BudgetProjectionEntry) {
                                const budgetProjectionEntry = storeDataEntry.budgetProjectionEntries?.find(
                                    budgetProjectionEntry => moment.utc(budgetProjectionEntry.date)
                                        .isSame(entity.date, 'month'),
                                );

                                storeColumns.push(
                                    new DataColumn(
                                        new BudgetProjectionEntry(budgetProjectionEntry ?? {date: entity.date}, false),
                                    ),
                                );
                            }
                        },
                    );

                    storeData.push({
                        store: store,
                        budgetId: storeDataEntry.budgetId,
                        columns: new DataColumnCollection(storeColumns, true),
                        results: storeDataEntry,
                    });
                });
            }

            this.collection.columns = columns;
            this.additionalColumns.columns = additionalColumns;
            this.storeData = storeData;
        },
    },
};
</script>
